import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import GavelIcon from "@mui/icons-material/Gavel";
import LaunchIcon from "@mui/icons-material/Launch";
import SearchIcon from "@mui/icons-material/Search";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import BalanceIcon from "@mui/icons-material/Balance";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SecurityIcon from "@mui/icons-material/Security";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import BiotechIcon from "@mui/icons-material/Biotech";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import SummarizeIcon from "@mui/icons-material/Summarize";
import { getImageUrl } from "../../../utils/helpers";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";

const scrollToSection = (id) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: "smooth" });
  }
};

const AiLitigation = ({ baseConfig }) => {
  const { priColor, priHoverColor, tertColor, tertHoverColor } =
    baseConfig.siteBranding;

  // Litigation support services
  const litigationServices = [
    {
      title: "Pre-Litigation Rule 11 Investigations",
      icon: <ManageSearchIcon />,
      description:
        "Thorough analysis of AI systems to establish factual basis for claims while protecting attorney work product",
    },
    {
      title: "AI-Generated Output Evaluation",
      icon: <AssessmentIcon />,
      description:
        "Technical and legal analysis of AI-generated content for accuracy, consistency, and evidentiary strength",
    },
    {
      title: "Expert Witness Coordination",
      icon: <BusinessCenterIcon />,
      description:
        "Identification and preparation of technical experts qualified to testify on AI-related matters",
    },
    {
      title: "Evidence Preservation Strategy",
      icon: <SecurityIcon />,
      description:
        "Development of protocols for preserving AI system logs, training data, and other digital evidence",
    },
  ];

  // AI litigation challenges
  const litigationChallenges = [
    {
      title: "Black Box Problem",
      description:
        "Addressing challenges in explaining how complex AI systems reach specific conclusions",
    },
    {
      title: "Model Version Control",
      description:
        "Tracking AI model changes over time to identify causation in legal disputes",
    },
    {
      title: "Training Data Provenance",
      description:
        "Establishing the legitimacy and ownership of data used to train AI systems",
    },
    {
      title: "Performance Metrics & Standards",
      description:
        "Defining appropriate benchmarks for AI accuracy and reliability in legal contexts",
    },
  ];

  return (
    <Box sx={{ mb: 6 }}>
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          backgroundColor: `${tertColor}22`,
          borderRadius: "4px 4px 0 0",
        }}
      >
        <BalanceIcon sx={{ mr: 2, color: tertColor }} />
        <Typography variant="h5" fontWeight="bold">
          AI in Litigation
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: `1px solid ${tertColor}33`,
          borderTop: 0,
          borderRadius: "0 0 4px 4px",
          mb: 4,
        }}
      >
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography
              variant="h6"
              gutterBottom
              fontWeight="bold"
              color={tertColor}
            >
              AI Litigation Support Services
            </Typography>
            <Typography variant="body1" paragraph>
              Kama Thuo, PLLC supports law firms and corporate clients with
              comprehensive litigation-related AI services. We conduct
              meticulous pre-litigation Rule 11 investigations of AI
              applications and systems, ensuring thorough analysis while
              safeguarding attorney work product from unintended discovery.
            </Typography>

            <Typography variant="body1" paragraph>
              We also evaluate AI-generated legal outputs for accuracy,
              consistency, and evidentiary strength, providing critical support
              in technologically complex litigation. Our AI expertise enables us
              to advise on evidentiary admissibility, AI model defensibility,
              and strategy in high-stakes technology disputes.
            </Typography>

            <Typography variant="body1" paragraph>
              Our technical background allows us to bridge the gap between
              complex AI technologies and legal requirements, helping litigation
              teams effectively prepare cases involving artificial intelligence
              components. We work alongside litigation counsel to provide
              specialized technical and legal insight specific to AI systems.
            </Typography>

            <Box sx={{ mt: 3 }}>
              <Button
                startIcon={<LaunchIcon />}
                variant="contained"
                sx={{
                  backgroundColor: priColor,
                  color: "#fff",
                  "&:hover": { backgroundColor: priHoverColor },
                }}
                component={RouterLink}
                to="/news-insights?practice=ai"
              >
                AI News & Insights
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src={getImageUrl("kth-ai-litigation.png")}
              alt="AI in Litigation"
              sx={{
                width: "100%",
                height: "auto",
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                mb: 3,
              }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Card
              elevation={2}
              sx={{
                borderRadius: 2,
                border: `1px solid ${tertColor}33`,
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  fontWeight="bold"
                  color={tertColor}
                >
                  How We Support Litigation Teams
                </Typography>

                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <SearchIcon sx={{ color: tertColor }} />
                    </ListItemIcon>
                    <ListItemText primary="Technical discovery assistance to identify relevant AI system components" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <FactCheckIcon sx={{ color: tertColor }} />
                    </ListItemIcon>
                    <ListItemText primary="Analysis of AI outputs for potential claims and defenses" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <BalanceIcon sx={{ color: tertColor }} />
                    </ListItemIcon>
                    <ListItemText primary="Expert guidance on emerging AI legal standards and precedents" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <BiotechIcon sx={{ color: tertColor }} />
                    </ListItemIcon>
                    <ListItemText primary="Preparation of technically sound strategies for AI-related claims" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <SummarizeIcon sx={{ color: tertColor }} />
                    </ListItemIcon>
                    <ListItemText primary="Creation of clear explanations of complex AI concepts for judges and juries" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        <Box>
          <Typography
            variant="h6"
            gutterBottom
            fontWeight="bold"
            color={tertColor}
          >
            Our Litigation Support Services
          </Typography>

          <Grid container spacing={3}>
            {litigationServices.map((service, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  elevation={1}
                  sx={{
                    height: "100%",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                    },
                  }}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Box sx={{ color: tertColor, mr: 1.5 }}>
                        {service.icon}
                      </Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {service.title}
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="body2">
                      {service.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box>
          <Typography
            variant="h6"
            gutterBottom
            fontWeight="bold"
            color={tertColor}
          >
            Unique Challenges in AI Litigation
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Typography variant="body1" paragraph>
                Artificial intelligence presents unique evidentiary and
                procedural challenges in litigation that traditional legal
                frameworks are not fully equipped to address. Our firm helps
                litigation teams navigate these complexities, developing
                strategies that account for the technical nuances of AI systems
                while meeting legal standards for evidence and proof.
              </Typography>

              <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                Understanding both the technical aspects of AI and the
                applicable legal standards is essential for effectively
                litigating cases involving artificial intelligence. Our combined
                technical and legal expertise provides litigation teams with the
                insights needed to develop sound legal strategies in this
                emerging area.
              </Typography>

              <Grid container spacing={2}>
                {litigationChallenges.map((challenge, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box sx={{ display: "flex", mb: 2 }}>
                      <VerifiedUserIcon
                        sx={{ color: tertColor, mr: 1.5, mt: 0.5 }}
                      />
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {challenge.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {challenge.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Box sx={{ mt: 2 }}>
                <Typography variant="body1" paragraph>
                  Our firm does not handle litigation directly but provides
                  specialized technical and legal support to litigation teams.
                </Typography>

                <Button
                  startIcon={<PhoneInTalkIcon />}
                  variant="contained"
                  onClick={() => scrollToSection("contact-section")}
                  sx={{
                    backgroundColor: tertColor,
                    color: "white",
                    "&:hover": {
                      color: "white",
                      backgroundColor: tertHoverColor,
                    },
                  }}
                >
                  Schedule a Consultation
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} md={5}>
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  backgroundColor: "#f9f9f9",
                  borderRadius: 2,
                  border: `1px solid ${tertColor}33`,
                }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  fontWeight="bold"
                  color={tertColor}
                >
                  Types of AI-Related Litigation
                </Typography>

                <List>
                  <ListItem>
                    <ListItemIcon>
                      <GavelIcon sx={{ color: tertColor }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Patent Infringement"
                      secondary="Disputes involving AI algorithms, methods, and systems"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <GavelIcon sx={{ color: tertColor }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Copyright Claims"
                      secondary="Issues related to training data and AI-generated content"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <GavelIcon sx={{ color: tertColor }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Contract Disputes"
                      secondary="Performance issues in AI development and deployment agreements"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <GavelIcon sx={{ color: tertColor }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Regulatory Enforcement"
                      secondary="Actions related to AI compliance with emerging regulations"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <GavelIcon sx={{ color: tertColor }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Bias & Discrimination Claims"
                      secondary="Allegations related to algorithmic bias or unfair outcomes"
                    />
                  </ListItem>
                </List>

                {/* <Box sx={{ mt: 2, textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Our firm does not handle litigation directly but provides
                    specialized technical and legal support to litigation teams.
                  </Typography>
                  <Button
                    variant="contained"
                    endIcon={<LaunchIcon />}
                    component={RouterLink}
                    to="/contact"
                    sx={{
                      backgroundColor: tertColor,
                      color: "white",
                      "&:hover": {
                        color: "white",
                        backgroundColor: tertHoverColor,
                      },
                    }}
                  >
                    Contact Us About Litigation Support
                  </Button>
                </Box> */}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default AiLitigation;
